/* Copyright (c) 2025 Xperi. All Rights Reserved.  */

import {
    ProgramType,
    ToDoGroupHeader,
    ToDoItem,
    ToDoOfferItem,
    ToDoOfferItemCancelReasonEnum,
    ToDoOfferItemToDoStatusEnum,
    ToDoType,
} from 'client-code/recordings';

import { LEFT_QUOTE, RIGHT_QUOTE } from '../../../../config';
import { Locale, LocaleKeys } from '../../../../core/locale';
import { BrandingUiUtility } from '../../../../core/utils';

export const LIST_ROWS_ON_PAGE = 15;
export const LIST_ITEMS_MAX_COUNT = 500;

export const getToDoListOfferItemIconClassname = (item: ToDoOfferItem) => {
    switch (item.toDoStatus) {
        case ToDoOfferItemToDoStatusEnum.Conflict:
            return 'todo-conflict';
        case ToDoOfferItemToDoStatusEnum.ScheduledOnepass:
            return 'todo-scheduled-as-onepass';
        case ToDoOfferItemToDoStatusEnum.SingleExplicitRecord:
            return 'todo-scheduled-as-single-explicit';
        case ToDoOfferItemToDoStatusEnum.RecordingInProgress:
            return 'todo-in-progress-recording';

        default:
            return '';
    }
};

export const getTodoOfferItemTitle = (item: ToDoOfferItem) => {
    return item.episodeTitle
        ? `${item.title}: ${LEFT_QUOTE}${item.episodeTitle}${RIGHT_QUOTE}`
        : item.title;
};

export const getTodoItemsFromPage = (toDoList: ToDoItem[], page: number) => {
    const offset = page * LIST_ROWS_ON_PAGE;
    const items = toDoList.slice(offset, offset + LIST_ROWS_ON_PAGE);
    const actualItems = items.filter(
        (item: ToDoItem) => item.toDoType === ToDoType.ToDoOfferItem,
    ) as ToDoOfferItem[];
    const actualItemsLength = items.filter(
        (item: ToDoItem) => item.toDoType === ToDoType.ToDoOfferItem,
    ).length;

    return { items, actualItems, actualItemsLength };
};

export const isToDoGroupHeader = (item: ToDoItem): item is ToDoGroupHeader => {
    return item.toDoType === ToDoType.ToDoGroupHeader;
};

export const mapToDoItemConflictToMessageKey = (item: ToDoOfferItem) => {
    let episodeType = '';
    let episodeTypePlural = '';
    let episodeTypeWithArticle = '';
    let title = '';
    let description = '';
    let preview = '';
    switch (item.programType) {
        case ProgramType.Episode:
            episodeType = Locale.getString(LocaleKeys.EPISODE);
            episodeTypePlural = Locale.getString(LocaleKeys.EPISODE_PLURAL);
            episodeTypeWithArticle = Locale.getString(LocaleKeys.EPISODE_WITH_ARTICLE);
            break;
        case ProgramType.Event:
            episodeType = Locale.getString(LocaleKeys.EVENT);
            episodeTypePlural = Locale.getString(LocaleKeys.EVENT_PLURAL);
            episodeTypeWithArticle = Locale.getString(LocaleKeys.EVENT_WITH_ARTICLE);
            break;
        case ProgramType.Game:
            episodeType = Locale.getString(LocaleKeys.GAME);
            episodeTypePlural = Locale.getString(LocaleKeys.GAME_PLURAL);
            episodeTypeWithArticle = Locale.getString(LocaleKeys.GAME_WITH_ARTICLE);
            break;
        case ProgramType.Race:
            episodeType = Locale.getString(LocaleKeys.RACE);
            episodeTypePlural = Locale.getString(LocaleKeys.RACE_PLURAL);
            episodeTypeWithArticle = Locale.getString(LocaleKeys.RACE_WITH_ARTICLE);
            break;
        case ProgramType.Show:
            episodeType = Locale.getString(LocaleKeys.SHOW);
            episodeTypePlural = Locale.getString(LocaleKeys.SHOW_PLURAL);
            episodeTypeWithArticle = Locale.getString(LocaleKeys.SHOW_WITH_ARTICLE);
            break;
        case ProgramType.Movie:
            episodeType = Locale.getString(LocaleKeys.MOVIE);
            episodeTypePlural = Locale.getString(LocaleKeys.MOVIE_PLURAL);
            episodeTypeWithArticle = Locale.getString(LocaleKeys.MOVIE_WITH_ARTICLE);
            break;
        case ProgramType.Match:
            episodeType = Locale.getString(LocaleKeys.MATCH);
            episodeTypePlural = Locale.getString(LocaleKeys.MATCH_PLURAL);
            episodeTypeWithArticle = Locale.getString(LocaleKeys.MATCH_WITH_ARTICLE);
            break;

        default:
    }

    switch (item.cancelReason) {
        case ToDoOfferItemCancelReasonEnum.GenericUnknown:
            title = Locale.getString(LocaleKeys.GENERIC_UNKNOWN_TITLE);
            description = Locale.getString(LocaleKeys.GENERIC_UNKNOWN_BODY);
            preview = Locale.getString(LocaleKeys.GENERIC_UNKNOWN_PREVIEW);
            break;
        case ToDoOfferItemCancelReasonEnum.Notauthorized:
            title = Locale.getString(LocaleKeys.NOT_AUTHORIZED_TITLE);
            description = Locale.getString(LocaleKeys.NOT_AUTHORIZED_BODY);
            preview = Locale.getString(LocaleKeys.NOT_AUTHORIZED_PREVIEW);
            break;
        case ToDoOfferItemCancelReasonEnum.Notfirstrun:
            title = Locale.getString(LocaleKeys.NOT_FIRST_RUN_TITLE);
            description = Locale.getString(LocaleKeys.NOT_FIRST_RUN_BODY);
            preview = Locale.getString(LocaleKeys.NOT_FIRST_RUN_PREVIEW);
            break;
        case ToDoOfferItemCancelReasonEnum.Resolutiontoolow:
            title = Locale.getString(LocaleKeys.RESOLUTION_TOO_LOW_TITLE);
            description = Locale.getString(LocaleKeys.RESOLUTION_TOO_LOW_BODY);
            preview = Locale.getString(LocaleKeys.RESOLUTION_TOO_LOW_PREVIEW);
            break;
        case ToDoOfferItemCancelReasonEnum.Notrecordedstoragequota:
            title = Locale.getString(LocaleKeys.NOT_RECORDED_STORAGE_QUOTA_TITLE);
            description = Locale.getString(LocaleKeys.NOT_RECORDED_STORAGE_QUOTA_BODY);
            preview = Locale.getString(LocaleKeys.NOT_RECORDED_STORAGE_QUOTA_PREVIEW);
            break;
        case ToDoOfferItemCancelReasonEnum.Notrecordedkeepatmost:
            title = Locale.getString(LocaleKeys.NOT_RECORDED_KEEP_AT_MOST_TITLE);
            description = Locale.getString(LocaleKeys.NOT_RECORDED_KEEP_AT_MOST_BODY);
            preview = Locale.getString(LocaleKeys.NOT_RECORDED_KEEP_AT_MOST_PREVIEW);
            break;
        case ToDoOfferItemCancelReasonEnum.Recordingdifferentshowing:
            title = Locale.getString(LocaleKeys.RECORDING_DIFFERENT_SHOWING_TITLE);
            description = Locale.getString(LocaleKeys.RECORDING_DIFFERENT_SHOWING_BODY);
            preview = Locale.getString(LocaleKeys.RECORDING_DIFFERENT_SHOWING_PREVIEW);
            break;
        default:
            title = Locale.getString(LocaleKeys.GENERIC_UNKNOWN_TITLE);
            description = Locale.getString(LocaleKeys.GENERIC_UNKNOWN_BODY);
            preview = Locale.getString(LocaleKeys.GENERIC_UNKNOWN_PREVIEW);
            break;
    }

    description = BrandingUiUtility.formatDescription(description, {
        EPISODE_TYPE: episodeType,
        EPISODE_TYPE_PLURAL: episodeTypePlural,
        EPISODE_TYPE_WITH_ARTICLE: episodeTypeWithArticle,
        TITLE: item.title,
    });

    return { title, description, preview };
};
